<template>
  <div class="presenter-container">
    <div v-if="isAuthenticated" class="go-back" @click="$router.go(-1)"><i class="fa fa-chevron-left"/> Project Details</div>

    <transition name="fade">
      <div v-if="loading" class="loading" style="text-align:center;">
        <img src="../../assets/spinner.svg" alt="loading" style="margin:auto;">
      </div>
    </transition>
    <div v-if="suggestions.length < 1 && !loading" class="no-suggestions">
      Looks like there aren't any designs to present yet.
    </div>
    <div v-if="currentSuggestion" class="main room">
      <img class="bgimage" :src="this.roomAccessURL" @load="setBgDimensions" ref="bgimage"/>
      <presenter-product v-for="(p, i) in currentSuggestion.products" 
        :product="p" 
        :backgroundImage="backgroundImage"
        :pixelPerUnit="pixelPerUnit"
        :key="i"/>

      <div class="control left" @click="goLeft()">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
      </div>
      <div class="control right" @click="goRight()">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-right"><polyline points="9 18 15 12 9 6"></polyline></svg>
      </div>

      <div class="nav-indicator">
        <div v-for="(i, j) in suggestions.length" 
          @click="currentIndex = j"
          :class="{active : j == currentIndex}" 
          :key="i"
          class="nav-index-item" >
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </div>
      </div>
    </div> 
 
  </div>
</template>

<script>
import { deURLify } from '@/utils/urlify.js'

import projectService from '@/services/project-service.js'

import PresenterProduct from '@/components/designer/PresenterProduct.vue'

export default {
  name: 'Presenter',
  components: {
    PresenterProduct,
  },
  props: {
    user: {
      type: String,
      required: true,
    },
    projectName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      suggestions: [],
      currentIndex: 0,

      backgroundImage: {x: 0, y: 0, width: 0, height: 0},

      loading: false
    }
  },
  async created() {
    window.addEventListener('resize', this.setBgDimensions)
    window.addEventListener('keydown', this.handleKeypress)

    this.loading = true
    if(!this.projectInStore || !this.projectInStore.accessURLs) {
      this.$store.dispatch('getProject', deURLify(this.projectName))
    }
    this.suggestions = await projectService.loadSuggestions(deURLify(this.projectName))
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setBgDimensions)
    window.removeEventListener('keydown', this.handleKeypress)
  },
  methods: {
    handleKeypress(e) {
      if(e.key == 'ArrowRight') {
        this.goRight()
      }
      if(e.key == 'ArrowLeft') {
        this.goLeft()
      }
    },
    goLeft() {
      if(this.currentIndex > 0)
        this.currentIndex--
    },
    goRight() {
      if(this.currentIndex < this.suggestions.length-1)
        this.currentIndex++
    },
    setBgDimensions() {
      const box = this.$refs.bgimage.getBoundingClientRect()
      const bg = {
        x: box.left,
        y: box.top,
        width: box.width,
        height: box.height
      }
      this.$store.commit('mutate', { prop: 'backgroundImage', value: bg })
    },
  },
  computed: {
    sortedSuggestions() {
      return this.suggestions.slice().sort((a, b) => (a.room > b.room) ? 1 : -1)
    },
    currentSuggestion() {
      if(this.sortedSuggestions.length > this.currentIndex)
        return this.sortedSuggestions[this.currentIndex]
      return null
    },
    roomAccessURL() {
      if(this.currentSuggestion) 
        return this.$store.getters.roomAccessURL(this.currentSuggestion.room)
      return ''
    },
    pixelPerUnit() {
      return this.backgroundImage.width * this.currentSuggestion.calibration
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    }
  },
  watch: {
    suggestions() {
      if(this.suggestions.length > 0) {
        this.loading = false
        this.suggestions.forEach(s => {
          let i = new Image()
          i.src = this.$store.getters.roomAccessURL(s.room)
        })
      }
    },
    currentSuggestion() {
      this.$store.commit('mutate', {prop: 'percentBackgroundWidthPerUnit', value: this.currentSuggestion.calibration })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/shared.scss';

.presenter-container {
  background-color: white;
  height: 100vh;
  display: flex;
}

.go-back {
  color: $primaryText;
  font-size: 20px;
  opacity: 0;
  transition: opacity 0.5s;

  &:hover {
    opacity: 1;
  }
}

.room {
  flex: 1;
  max-width: 100vw;
  max-height: 100vh;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  display: inline-block;

  img {
    max-width: 100vw;
    max-height: 100vh;
    pointer-events: none;
    user-select: none;
  }
}

.control {
  position: fixed;
  top: 50%;
  color: $primaryText;
  padding: 30px;
  transform: translate(0, -50%);
  cursor: pointer;
}
.left {
  left: 20px;
}
.right {
  right: 20px;
}

.nav-indicator {
  position: fixed;
  bottom: 5px;
  right: 10px;
  color: rgb(204, 204, 204);

  .nav-index-item {
    display: inline-block;
    cursor: pointer;
  }
  .active {
    color: rgb(124, 124, 124);
  }
}

.loading {
  position: fixed;
  top: 50%;
  left:50%;
  transform: translate(-50%, -50%);
}


.no-suggestions {
  width: 100%;
  font-size: 20px;
  margin-top: 100px;
}

.slide-enter-active, .slide-leave-active {
  transition: all 1s ease-out;
}

.slide-enter {
  transform: translateX(-100%);
}

.slide-leave-to {
  transform: translateX(100%);
}
</style>